import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clsx from 'clsx';
import { BackgroundData } from 'src/api/plan';
import { useFullscreenElement } from 'src/providers/FullscreenProvider/FullscreenProvider';
import { falsy } from 'src/utils/falsy';
import { precision } from 'src/utils/precision';
import { throttle } from 'src/utils/throttle';
import { toGlobalPosition } from 'src/utils/toGlobalPosition';

import { FullscreenModeButton } from '../FullscreenModeButton/FullscreenModeButton';

import { BookingObjects } from './BookingObjects';
import classes from './FloorMap.module.scss';
import { useFloorMap } from './FloorMapProvider';
import { ZoomButtons } from './ZoomButtons/ZoomButtons';
import { Point, RenderBookingObjectParams } from './types';
import { calculateNextZoom } from './utils';

const getEventCoordinate = (
  pageCoord: number,
  containerOffset: number,
  containerSize: number,
): number => {
  return Math.max(Math.min(pageCoord - containerOffset, containerSize), 0);
};

export enum MapMode {
  AddMark,
  Grab,
  GrabbingMark,
  GrabbingMap,
}

export interface FloorMapBackgroundProps<T> {
  backgroundData: BackgroundData;
  bookingObjects: Map<string, T>;
  onClickOnMap?: (point: Point) => void;
  onCursorMove?: (point: Point) => void;
  onCursorLeave?: () => void;
  onClickOnBookingObject?: (id: string) => void;
  onHoverOnBookingObject?: (id: string) => void;
  onDragBookingObject?: (point: Point) => void;
  onMoveMap?: () => void;
  renderBookingObject: (params: RenderBookingObjectParams<T>) => ReactNode;
  supportsDragging?: (id: string) => boolean;
  supportsAdd?: boolean;
  touchDevice?: boolean;
  fullscreenIsSupported?: boolean;
}

export const FloorMapBackground = <T,>({
  backgroundData,
  bookingObjects,
  onClickOnMap,
  onCursorMove,
  onCursorLeave,
  onClickOnBookingObject,
  onHoverOnBookingObject,
  onDragBookingObject,
  onMoveMap,
  renderBookingObject,
  supportsDragging = falsy,
  supportsAdd,
  touchDevice,
  fullscreenIsSupported,
}: FloorMapBackgroundProps<T>): ReactElement => {
  const {
    zoomRef,
    zoomValue,
    viewportRef,
    observeContainer,
    containerDimensionsRef,
    zoom,
    backgroundOffset,
    backgroundOffsetRef,
    setBackgroundOffset,
    isAnimating,
  } = useFloorMap();
  const pointerStartPoint = useRef<Point | null>(null);
  const touchStartPoint = useRef<Point | null>(null);
  const touchDistance = useRef<number | null>(null);
  const initialBackgroundOffsetRef = useRef<Point>(backgroundOffsetRef.current);
  const placeId = useRef<string | null>(null);
  const defaultMode = supportsAdd ? MapMode.AddMark : MapMode.Grab;
  const [mode, setMode] = useState<MapMode>(defaultMode);
  const grabbingMap = useRef(false);
  const modeRef = useRef<MapMode>(defaultMode);

  const changeMode = useCallback((value: MapMode) => {
    modeRef.current = value;
    setMode(value);
  }, []);

  const getCoordinate = useCallback(
    (event: MouseEvent | PointerEvent): Point => {
      const rect = viewportRef.current?.getBoundingClientRect();

      return {
        x: getEventCoordinate(
          event.pageX,
          rect?.left ?? 0,
          containerDimensionsRef.current.width,
        ),
        y: getEventCoordinate(
          event.pageY,
          rect?.top ?? 0,
          containerDimensionsRef.current.height,
        ),
      };
    },
    [containerDimensionsRef, viewportRef],
  );

  const getTouchDistance = useCallback(
    (event: TouchEvent): number => {
      const touches = [...event.touches];

      if (touches.length < 2) return 0;

      const rect = viewportRef.current?.getBoundingClientRect();

      const first = {
        x: getEventCoordinate(
          touches[0].pageX,
          rect?.left ?? 0,
          containerDimensionsRef.current.width,
        ),
        y: getEventCoordinate(
          touches[0].pageY,
          rect?.top ?? 0,
          containerDimensionsRef.current.height,
        ),
      };
      const second = {
        x: getEventCoordinate(
          touches[1].pageX,
          rect?.left ?? 0,
          containerDimensionsRef.current.width,
        ),
        y: getEventCoordinate(
          touches[1].pageY,
          rect?.top ?? 0,
          containerDimensionsRef.current.height,
        ),
      };

      return Math.hypot(first.x - second.x, first.y - second.y);
    },
    [containerDimensionsRef, viewportRef],
  );

  const handleZoom = useCallback(
    (event: WheelEvent) => {
      const nextZoom = calculateNextZoom(event.deltaY < 0, zoomRef.current);
      const rect = viewportRef.current?.getBoundingClientRect();

      zoom(nextZoom, {
        x: event.pageX - (rect?.left ?? 0),
        y: event.pageY - (rect?.top ?? 0),
      });
    },
    [viewportRef, zoom, zoomRef],
  );

  const throttledHandleZoom = useMemo(
    () => throttle(handleZoom, 100),
    [handleZoom],
  );

  const moveMap = useCallback(
    (from: Point, to: Point) => {
      const delta: Point = {
        x: to.x - from.x,
        y: to.y - from.y,
      };

      const resultOffset = {
        x: initialBackgroundOffsetRef.current.x - delta.x,
        y: initialBackgroundOffsetRef.current.y - delta.y,
      };

      setBackgroundOffset(resultOffset);
      backgroundOffsetRef.current = resultOffset;
    },
    [backgroundOffsetRef, setBackgroundOffset],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      throttledHandleZoom(event);
    },
    [throttledHandleZoom],
  );

  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (!viewportRef.current) return;

      if (touchStartPoint.current) return;

      event.preventDefault();

      const place =
        event.target instanceof HTMLElement ||
        event.target instanceof SVGElement
          ? event.target.dataset.placeId
          : undefined;

      if (place && supportsDragging(place)) {
        placeId.current = place;
      } else {
        const { x, y } = getCoordinate(event);

        pointerStartPoint.current = { x, y };
        initialBackgroundOffsetRef.current = {
          x: backgroundOffsetRef.current.x,
          y: backgroundOffsetRef.current.y,
        };
      }
    },
    [backgroundOffsetRef, getCoordinate, supportsDragging, viewportRef],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!viewportRef.current) return;

      if (touchStartPoint.current) return;

      const place =
        event.target instanceof HTMLElement ||
        event.target instanceof SVGElement
          ? event.target.dataset.placeId
          : undefined;

      if (place) {
        onHoverOnBookingObject?.(place);
      }

      const { x, y } = getCoordinate(event);

      const point = {
        x: precision(
          toGlobalPosition({
            localPosition: x,
            offset: backgroundOffsetRef.current.x,
            localSize: containerDimensionsRef.current.width,
            globalSize: backgroundData.width,
            zoom: zoomRef.current,
          }),
          0,
        ),
        y: precision(
          toGlobalPosition({
            localPosition: y,
            offset: backgroundOffsetRef.current.y,
            localSize: containerDimensionsRef.current.height,
            globalSize: backgroundData.height,
            zoom: zoomRef.current,
          }),
          0,
        ),
      };

      if (!pointerStartPoint.current && placeId.current === null) {
        onCursorMove?.(point);
      }

      if (placeId.current !== null && supportsDragging(placeId.current)) {
        changeMode(MapMode.GrabbingMark);
        onDragBookingObject?.(point);
        return;
      }

      if (
        pointerStartPoint.current &&
        (pointerStartPoint.current.x !== x || pointerStartPoint.current.y !== y)
      ) {
        changeMode(MapMode.GrabbingMap);
        moveMap(pointerStartPoint.current, {
          x,
          y,
        });
        onMoveMap?.();
        grabbingMap.current = true;
      }
    },
    [
      viewportRef,
      getCoordinate,
      backgroundOffsetRef,
      containerDimensionsRef,
      backgroundData.width,
      backgroundData.height,
      zoomRef,
      supportsDragging,
      onHoverOnBookingObject,
      onCursorMove,
      changeMode,
      onDragBookingObject,
      moveMap,
      onMoveMap,
    ],
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (!viewportRef.current) return;

      if (
        modeRef.current === MapMode.GrabbingMap ||
        modeRef.current === MapMode.GrabbingMark
      ) {
        pointerStartPoint.current = null;
        initialBackgroundOffsetRef.current = backgroundOffsetRef.current;
        placeId.current = null;
      }

      if (modeRef.current === defaultMode) {
        const place =
          event.target instanceof HTMLElement ||
          event.target instanceof SVGElement
            ? event.target.dataset.placeId
            : undefined;
        if (place) placeId.current = place;
      }

      if (modeRef.current === MapMode.GrabbingMap) {
        changeMode(defaultMode);
        grabbingMap.current = false;
      }
    },
    [viewportRef, backgroundOffsetRef, changeMode, defaultMode],
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopImmediatePropagation();

      if (!viewportRef.current) return;

      if (modeRef.current === defaultMode) {
        if (placeId.current !== null) {
          onClickOnBookingObject?.(placeId.current);

          pointerStartPoint.current = null;
          changeMode(defaultMode);
          placeId.current = null;

          return;
        }
      }

      if (modeRef.current === MapMode.AddMark && !grabbingMap.current) {
        const { x, y } = getCoordinate(event);

        const point = {
          x: precision(
            toGlobalPosition({
              localPosition: x,
              offset: backgroundOffsetRef.current.x,
              localSize: containerDimensionsRef.current.width,
              globalSize: backgroundData.width,
              zoom: zoomRef.current,
            }),
            0,
          ),
          y: precision(
            toGlobalPosition({
              localPosition: y,
              offset: backgroundOffsetRef.current.y,
              localSize: containerDimensionsRef.current.height,
              globalSize: backgroundData.height,
              zoom: zoomRef.current,
            }),
            0,
          ),
        };

        onClickOnMap?.(point);
      }

      pointerStartPoint.current = null;
      changeMode(defaultMode);
      placeId.current = null;
      grabbingMap.current = false;
    },
    [
      viewportRef,
      changeMode,
      defaultMode,
      onClickOnBookingObject,
      getCoordinate,
      backgroundOffsetRef,
      containerDimensionsRef,
      backgroundData.width,
      backgroundData.height,
      zoomRef,
      onClickOnMap,
    ],
  );

  const handleCursorLeave = useCallback(() => {
    pointerStartPoint.current = null;
    changeMode(defaultMode);
    placeId.current = null;
    grabbingMap.current = false;

    onCursorLeave?.();
  }, [changeMode, defaultMode, onCursorLeave]);

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      const touches = [...event.touches];
      if (touches.length < 2) return;

      event.preventDefault();

      touchDistance.current = getTouchDistance(event);

      const first = touches[0];
      const second = touches[1];

      const xMin = Math.min(first.pageX, second.pageX);
      const xMax = Math.max(first.pageX, second.pageX);
      const yMin = Math.min(first.pageY, second.pageY);
      const yMax = Math.max(first.pageY, second.pageY);
      const middle: Point = {
        x: xMin + (xMax - xMin) / 2,
        y: yMin + (yMax - yMin) / 2,
      };

      touchStartPoint.current = middle;
    },
    [getTouchDistance],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      const touches = [...event.touches];
      if (touches.length < 2) return;

      if (!touchStartPoint.current) return;

      event.preventDefault();

      if (!touchDistance.current) return;

      const nextTouchDistance = getTouchDistance(event);

      const nextZoom =
        (zoomRef.current * nextTouchDistance) / touchDistance.current;
      touchDistance.current = nextTouchDistance;

      const rect = viewportRef.current?.getBoundingClientRect();

      zoom(nextZoom, {
        x: touchStartPoint.current.x - (rect?.left ?? 0),
        y: touchStartPoint.current.y - (rect?.top ?? 0),
      });
    },
    [getTouchDistance, viewportRef, zoom, zoomRef],
  );

  const handleTouchEnd = useCallback((event: TouchEvent) => {
    if (event.touches.length) return;

    touchStartPoint.current = null;
    touchDistance.current = 0;
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) return;

    viewport.addEventListener('wheel', handleWheel, {
      passive: false,
    });
    viewport.addEventListener('pointerdown', handlePointerDown, {
      passive: false,
    });
    viewport.addEventListener('pointermove', handlePointerMove, {
      passive: false,
    });
    viewport.addEventListener('pointerup', handlePointerUp, {
      passive: false,
    });
    viewport.addEventListener('click', handleClick, {
      passive: false,
    });
    if (touchDevice) {
      viewport.addEventListener('touchstart', handleTouchStart);
      viewport.addEventListener('touchmove', handleTouchMove);
      viewport.addEventListener('touchend', handleTouchEnd);
    } else {
      viewport.addEventListener('pointerleave', handleCursorLeave, {
        passive: false,
      });
    }

    return () => {
      viewport.removeEventListener('wheel', handleWheel);
      viewport.removeEventListener('pointerdown', handlePointerDown);
      viewport.removeEventListener('pointermove', handlePointerMove);
      viewport.removeEventListener('pointerup', handlePointerUp);
      viewport.removeEventListener('click', handleClick);
      if (touchDevice) {
        viewport.removeEventListener('touchstart', handleTouchStart);
        viewport.removeEventListener('touchmove', handleTouchMove);
        viewport.removeEventListener('touchend', handleTouchEnd);
      } else {
        viewport.removeEventListener('pointerleave', handleCursorLeave);
      }
    };
  }, [
    handleClick,
    handleCursorLeave,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    handleWheel,
    onCursorLeave,
    touchDevice,
    viewportRef,
  ]);

  const backgroundStyle = useMemo(
    () => ({
      transform: `translate(${-backgroundOffset.x}px, ${-backgroundOffset.y}px) scale(${zoomValue})`,
      top: `calc(50% - ${backgroundData.height / 2}px)`,
      left: `calc(50% - ${backgroundData.width / 2}px)`,
    }),
    [
      backgroundData.height,
      backgroundData.width,
      backgroundOffset.x,
      backgroundOffset.y,
      zoomValue,
    ],
  );

  const { fullscreen, openFullscreen, closeFullscreen } =
    useFullscreenElement();

  return (
    <div
      ref={observeContainer}
      className={clsx(classes.container, classes.border)}
      data-testid='floorMapWithBackground'
    >
      <div className={classes.controls}>
        <ZoomButtons />
        {fullscreenIsSupported && (
          <div className={classes.buttonContainer}>
            <FullscreenModeButton
              fullscreen={fullscreen}
              openFullscreen={openFullscreen}
              closeFullscreen={closeFullscreen}
            />
          </div>
        )}
      </div>
      <div
        ref={viewportRef}
        className={clsx(
          classes.viewport,
          mode === MapMode.AddMark && classes.addMarkMode,
          (mode === MapMode.GrabbingMark || mode === MapMode.GrabbingMap) &&
            classes.grabbingMode,
          mode === MapMode.Grab && classes.grabMode,
        )}
        data-testid='floorMapWithBackgroundViewport'
      >
        <div
          className={clsx(
            classes.backgroundWrapper,
            isAnimating && classes.backgroundWrapperAnimating,
          )}
          style={backgroundStyle}
        >
          <div className={classes.background}>
            <img
              className={classes.backgroundImage}
              src={backgroundData.url}
              width={backgroundData.width}
              height={backgroundData.height}
              data-testid='floorMapWithBackgroundImage'
            />
            <BookingObjects
              items={bookingObjects}
              renderBookingObject={renderBookingObject}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
